import React, { useEffect, useState } from 'react';
import { Search, RefreshCw, Check, X as XIcon, Trophy, UserPlus, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import type { Entry, Campaign, Prize } from '../lib/supabase';
import ManualEntryForm from './admin/ManualEntryForm';
import WinnerManagement from './admin/WinnerManagement';
import RaffleDrawAnimation from './admin/RaffleDrawAnimation';
import { sendEmail, emailTemplates } from '../lib/email';
import { sendDrawStartNotifications } from '../lib/notifications';

type EntryWithCampaign = {
  id: string;
  campaign_id: string;
  email: string;
  name: string | null;
  phone_number?: string;
  ticket_number: string;
  payment_status: 'pending' | 'paid' | 'failed';
  payment_method: 'online' | 'inPerson';
  created_at: string;
  is_winner: boolean;
  campaign: Campaign;
  prize?: Prize;
  total_amount?: number;
  quantity?: number;
  prize_id?: string;
};

type CampaignWithEntries = Campaign & {
  entries: EntryWithCampaign[];
  _isExpanded?: boolean;
};

const AdminDashboard = () => {
  const [campaigns, setCampaigns] = useState<CampaignWithEntries[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingPayment, setUpdatingPayment] = useState<string | null>(null);
  const [selectedWinner, setSelectedWinner] = useState<EntryWithCampaign | null>(null);
  const [showWinnerManagement, setShowWinnerManagement] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showDrawConfirmation, setShowDrawConfirmation] = useState(false);
  const [showDrawAnimation, setShowDrawAnimation] = useState(false);
  const [drawingEntries, setDrawingEntries] = useState<Entry[]>([]);
  const [drawingPrizes, setDrawingPrizes] = useState<Prize[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaignsAndEntries();
  }, []);

  const fetchCampaignsAndEntries = async () => {
    try {
      setLoading(true);

      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch campaigns
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      // Fetch entries with prizes
      const { data: entriesData, error: entriesError } = await supabase
        .from('entries')
        .select(`
          *,
          campaign:campaign_id (*),
          prize:prize_id (*)
        `)
        .order('created_at', { ascending: false });

      if (entriesError) throw entriesError;

      // Combine campaigns with their entries
      const campaignsWithEntries = campaignsData.map(campaign => ({
        ...campaign,
        entries: entriesData
          .filter(entry => entry.campaign_id === campaign.id)
          .map(entry => ({
            ...entry,
            campaign: entry.campaign,
            prize: entry.prize
          })),
        _isExpanded: campaign.id === selectedCampaign
      }));

      setCampaigns(campaignsWithEntries);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawRaffle = async (campaignId: string) => {
    try {
      // First, check if there are any existing winners
      const { data: existingWinners, error: winnersError } = await supabase
        .from('entries')
        .select('prize_id')
        .eq('campaign_id', campaignId)
        .eq('is_winner', true);

      if (winnersError) throw winnersError;

      if (existingWinners && existingWinners.length > 0) {
        const confirmRedraw = window.confirm(
          'There are already winners selected for this raffle. Drawing again will remove existing winners. Are you sure you want to continue?'
        );

        if (!confirmRedraw) return;

        // Reset existing winners
        const { error: resetError } = await supabase
          .from('entries')
          .update({
            is_winner: false,
            prize_id: null
          })
          .eq('campaign_id', campaignId)
          .eq('is_winner', true);

        if (resetError) throw resetError;
      }

      // Send draw start notifications
      await sendDrawStartNotifications(campaignId);

      // Get all paid entries for this campaign
      const { data: paidEntries, error: entriesError } = await supabase
        .from('entries')
        .select(`
          *,
          campaign:campaign_id (
            id,
            name
          )
        `)
        .eq('campaign_id', campaignId)
        .eq('payment_status', 'paid')
        .eq('is_winner', false);

      if (entriesError) throw entriesError;
      if (!paidEntries?.length) {
        alert('No eligible entries found for the raffle');
        return;
      }

      // Get available prizes for the campaign
      const { data: campaignPrizes, error: prizesError } = await supabase
        .from('prizes')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('rank', { ascending: true });

      if (prizesError) throw prizesError;
      if (!campaignPrizes?.length) {
        alert('No prizes found for this campaign');
        return;
      }

      setDrawingEntries(paidEntries);
      setDrawingPrizes(campaignPrizes);
      setShowDrawAnimation(true);
    } catch (error) {
      console.error('Error preparing raffle draw:', error);
      alert('Failed to prepare raffle draw. Please try again.');
    }
  };

  const handleDrawComplete = async (winners: Array<{ ticket_number: string; prize: Prize }>) => {
    try {
      // Update winners in database
      for (const winner of winners) {
        const entry = drawingEntries.find(e => e.ticket_number === winner.ticket_number);
        if (!entry) continue;

        await supabase
          .from('entries')
          .update({
            is_winner: true,
            prize_id: winner.prize.id
          })
          .eq('id', entry.id);

        // Send winner notification
        if (entry.email) {
          await sendEmail({
            to: entry.email,
            ...emailTemplates.winnerAnnouncement(
              entry.name || 'Participant',
              winner.prize.name
            )
          });
        }
      }

      // Update campaign status
      if (winners.length > 0) {
        await supabase
          .from('campaigns')
          .update({ status: 'completed' })
          .eq('id', drawingEntries[0].campaign_id);
      }

      // Refresh data
      await fetchCampaignsAndEntries();

      // Show first winner in management panel
      const firstWinner = winners[0];
      const entry = drawingEntries.find(e => e.ticket_number === firstWinner.ticket_number);
      if (entry) {
        setSelectedWinner({
          ...entry,
          prize: firstWinner.prize,
          is_winner: true
        } as EntryWithCampaign);
        setShowWinnerManagement(true);
      }
    } catch (error) {
      console.error('Error finalizing raffle draw:', error);
      alert('Error updating winners. Please check the admin dashboard.');
    } finally {
      setShowDrawAnimation(false);
    }
  };

  const handleTogglePayment = async (entryId: string, currentStatus: string) => {
    try {
      setUpdatingPayment(entryId);
      const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';

      const { error } = await supabase
        .from('entries')
        .update({ payment_status: newStatus })
        .eq('id', entryId);

      if (error) throw error;

      // Refresh data
      await fetchCampaignsAndEntries();

      if (newStatus === 'paid') {
        const entry = campaigns
          .flatMap(c => c.entries)
          .find(e => e.id === entryId);

        if (entry && entry.email) {
          await sendEmail({
            to: entry.email,
            ...emailTemplates.paymentConfirmation(entry.ticket_number)
          });
        }
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status. Please try again.');
    } finally {
      setUpdatingPayment(null);
    }
  };

  const toggleCampaignExpansion = (campaignId: string) => {
    setCampaigns(campaigns.map(campaign => ({
      ...campaign,
      _isExpanded: campaign.id === campaignId ? !campaign._isExpanded : campaign._isExpanded
    })));
    setSelectedCampaign(campaignId);
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      campaign.entries.some(entry =>
        entry.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.ticket_number.includes(searchQuery) ||
        entry.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesSearch;
  });

  const handleSelectWinner = async (entry: EntryWithCampaign, prize: Prize) => {
    try {
      setLoading(true);

      // Update the entry with winner status and prize
      const { error: updateError } = await supabase
        .from('entries')
        .update({
          is_winner: true,
          prize_id: prize.id
        })
        .eq('id', entry.id);

      if (updateError) throw updateError;

      // Send winner announcement email
      if (entry.email) {
        await sendEmail({
          to: entry.email,
          ...emailTemplates.winnerAnnouncement(
            entry.name || 'Participant',
            prize.name
          )
        });
      }

      // Update the local state with all required properties
      const updatedEntry: EntryWithCampaign = {
        ...entry,
        is_winner: true,
        prize: prize
      };
      setSelectedWinner(updatedEntry);
      setShowWinnerManagement(true);
    } catch (error) {
      console.error('Error selecting winner:', error);
      alert('Failed to select winner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <p className="text-gray-600">Please log in to access the admin dashboard.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowManualEntry(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Manual Entry
              </button>
              <button
                onClick={fetchCampaignsAndEntries}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search raffles and entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-4">Loading data...</div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No raffles found</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entries
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCampaigns.map((campaign) => (
                    <React.Fragment key={campaign.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleCampaignExpansion(campaign.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {campaign._isExpanded ? (
                              <ChevronDown className="h-5 w-5" />
                            ) : (
                              <ChevronRight className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {campaign.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.entries.length} entries
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(campaign.start_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(campaign.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.status === 'active' && (
                            <button
                              onClick={() => {
                                setSelectedCampaign(campaign.id);
                                setShowDrawConfirmation(true);
                              }}
                              className="flex items-center px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                            >
                              <Trophy className="h-4 w-4 mr-1" />
                              Draw
                            </button>
                          )}
                        </td>
                      </tr>
                      {campaign._isExpanded && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-gray-50">
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Place
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Ticket #
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {campaign.entries.length === 0 ? (
                                    <tr>
                                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                        No entries found
                                      </td>
                                    </tr>
                                  ) : (
                                    campaign.entries.map((entry) => (
                                      <tr key={entry.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            entry.payment_status === 'paid'
                                              ? 'bg-green-100 text-green-800'
                                              : entry.payment_status === 'pending'
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : 'bg-red-100 text-red-800'
                                          }`}>
                                            {entry.payment_status}
                                          </span>
                                          {entry.is_winner && (
                                            <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                              Winner
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {entry.prize?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {entry.ticket_number}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {entry.name || 'Anonymous'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {entry.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {new Date(entry.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 space-x-2">
                                          <button
                                            onClick={() => handleTogglePayment(entry.id, entry.payment_status)}
                                            disabled={!!updatingPayment}
                                            className={`inline-flex items-center px-2 py-1 border rounded-md ${
                                              entry.payment_status === 'paid'
                                                ? 'border-red-300 text-red-700 hover:bg-red-50'
                                                : 'border-green-300 text-green-700 hover:bg-green-50'
                                            }`}
                                          >
                                            {updatingPayment === entry.id ? (
                                              <span>Updating...</span>
                                            ) : entry.payment_status === 'paid' ? (
                                              <>
                                                <XIcon className="h-4 w-4 mr-1" />
                                                Unpaid
                                              </>
                                            ) : (
                                              <>
                                                <Check className="h-4 w-4 mr-1" />
                                                Mark Paid
                                              </>
                                            )}
                                          </button>
                                          {campaign.status === 'active' && !entry.is_winner && entry.payment_status === 'paid' && (
                                            <button
                                              onClick={() => {
                                                const prize = drawingPrizes.length > 0 ? drawingPrizes[0] : null;
                                                if (prize) {
                                                  handleSelectWinner(entry, prize);
                                                } else {
                                                  alert('No prizes available for this campaign');
                                                }
                                              }}
                                              className="inline-flex items-center px-2 py-1 border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50"
                                            >
                                              <Trophy className="h-4 w-4 mr-1" />
                                              Winner
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showManualEntry && (
        <ManualEntryForm
          onClose={() => setShowManualEntry(false)}
          onEntryAdded={fetchCampaignsAndEntries}
          campaigns={campaigns}
        />
      )}

      {showWinnerManagement && selectedWinner && (
        <WinnerManagement
          winner={selectedWinner}
          onClose={() => setShowWinnerManagement(false)}
          onWinnerUpdated={fetchCampaignsAndEntries}
        />
      )}

      {showDrawConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Raffle Draw</h3>
            <p className="mb-4">
              Are you sure you want to draw winners for this raffle? This action will:
            </p>
            <ul className="list-disc pl-5 mb-4 text-sm">
              <li>Send notifications to all participants</li>
              <li>Randomly select winners for each prize</li>
              <li>Mark the campaign as completed</li>
            </ul>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDrawConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDrawConfirmation(false);
                  if (selectedCampaign) {
                    handleDrawRaffle(selectedCampaign);
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Draw Winners
              </button>
            </div>
          </div>
        </div>
      )}

      {showDrawAnimation && (
        <RaffleDrawAnimation
          entries={drawingEntries}
          prizes={drawingPrizes}
          onComplete={handleDrawComplete}
          onCancel={() => setShowDrawAnimation(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
